<script setup lang="ts">
import { computed } from "vue";

import LeJsonArticle from "@/components/articles/LeJsonArticle.vue";
import LeNgArticle from "@/components/articles/LeNgArticle.vue";
import LeNgArticleInfoBox from "@/components/articles/LeNgArticleInfoBox.vue";
import { useDictionariesStore } from "@/stores";
import type { Article, Dictionary, DictionaryMethod, SearchViewType } from "@/types";

const props = defineProps<{
  searchViewType: SearchViewType;
  article: Article;
  blurred?: boolean;
}>();

const emit = defineEmits<{
  search: [
    search: {
      query: string;
      methodId: string;
      auto: boolean;
      staticMetadata: string | undefined;
      temporaryMetadata: string[] | undefined;
      section: string | undefined;
    }
  ];
  expand: [methodId: string];
}>();

const { getDictionaryByMethodId, getMethodByMethodId } = useDictionariesStore();

const dictionary = computed<Dictionary | undefined>(() => getDictionaryByMethodId(props.article.methodId));
const method = computed<DictionaryMethod | undefined>(() => getMethodByMethodId(props.article.methodId));

const onSearch = (
  query: string,
  methodId: string,
  auto: boolean,
  staticMetadata: string | undefined,
  temporaryMetadata: string[] | undefined,
  section: string | undefined
) => {
  emit("search", {
    query,
    methodId,
    auto,
    staticMetadata,
    temporaryMetadata,
    section
  });
};
</script>

<template>
  <div class="le-exact-results-article">
    <slot name="overlay" />

    <template v-for="(data, i) in props.article.data" :key="i">
      <template v-if="data.html">
        <template v-if="Array.isArray(data.html)">
          <LeNgArticleInfoBox v-for="(blob, j) in data.html" :key="j" :html="blob" />
        </template>
        <LeNgArticle
          v-else
          :search-view-type="props.searchViewType"
          :method-id="props.article.methodId"
          :prefix="props.article.prefix ?? ''"
          :html="data.html"
          :index="i"
          :blurred="props.blurred"
          @ng-search="
            onSearch(
              $event.query,
              $event.methodId,
              $event.auto,
              $event.staticMetadata,
              $event.temporaryMetadata,
              $event.section
            )
          "
          @ng-expand="emit('expand', $event)"
        >
          <template #prefix>
            <slot name="prefix" />
          </template>
        </LeNgArticle>
      </template>
      <LeJsonArticle
        v-else-if="data.json"
        :article="data.json"
        :format="data.json.format"
        :dictionary="dictionary"
        :method="method"
        :search-view-type="props.searchViewType"
      />
    </template>
  </div>
</template>
